import { getDictionaryFromCDA } from './connector';
import { ActionKey, getDefaultActionValue } from './groups/ActionItems';
import { AuthenticationKey, getDefaultAuthenticationValue } from './groups/AuthenticationItems';
import { EventKey, getDefaultEventValue } from './groups/EventItems';
import { getDefaultContactValue, ContactKey } from './groups/ContactItems';
import { getDefaultPreviewValue, PreviewKey } from './groups/PreviewItems';
import { getDefaultSearchValue, SearchKey } from './groups/SearchItems';
import { getDefaultExplainerValue, ExplainerKey } from './groups/ExplainerItems';
import { getDefaultLanguageValue, LanguageKey } from './groups/LanguageItems';
import { getDefaultLabelValue, LabelKey } from './groups/LabelItems';
import { getDefaultNewsValue, NewsKey } from './groups/NewsItems';
import { DocumentKey, getDefaultDocumentValue } from './groups/DocumentItems';
import { getDefaultJobValue, JobKey } from './groups/JobItems';

export const defaultDictionaryValueMissing = 'Default dictionary value missing for';
export interface DictionaryInterface {
	getAction(key: ActionKey): string;
	getAuthentication(key: AuthenticationKey, replacements?: string[]): string;
	getEvent(key: EventKey, replacements?: string[]): string;
	getContact(key: ContactKey): string;
	getDocument(key: DocumentKey): string;
	getLabel(key: LabelKey): string;
	getNews(key: NewsKey): string;
	getJob(key: JobKey, locale?: string): string;
	getPreview(key: PreviewKey): string;
	getSearch(key: SearchKey, replacements?: string[]): string;
	getExplainer(key: ExplainerKey): string;
	getLanguage(key: LanguageKey, replacements?: string[]): string;
}

export const DictionaryFactory = (dictionary: Dictionary.Items): DictionaryInterface => {
	const getValue = ({
		group,
		key,
		defaultValue,
		replacements,
	}: {
		group:
			| 'Action'
			| 'Authentication'
			| 'Event'
			| 'Label'
			| 'News'
			| 'Contact'
			| 'Document'
			| 'Preview'
			| 'Search'
			| 'Language'
			| 'Explainer'
			| 'Job';
		key: string;
		defaultValue: string;
		replacements?: string[];
	}): string => {
		const dictionaryFromCms = dictionary.items?.find(
			(item) => item?.key.toLocaleLowerCase() === `${group.toLocaleLowerCase()}.${key.toLocaleLowerCase()}`,
		)?.value;
		const dictionaryValue = dictionaryFromCms ? dictionaryFromCms : defaultValue;
		if (!replacements) return dictionaryValue;
		replacements.forEach((replacement, index) => {
			return dictionaryValue.replace(`{value${index + 1}}`, replacement);
		});
		return dictionaryValue;
	};
	return {
		getAction: (key: ActionKey) => getValue({ group: 'Action', key, defaultValue: getDefaultActionValue(key) }),
		getAuthentication: (key: AuthenticationKey) => getValue({ group: 'Authentication', key, defaultValue: getDefaultAuthenticationValue(key) }),
		getEvent: (key: EventKey) => getValue({ group: 'Event', key, defaultValue: getDefaultEventValue(key) }),
		getContact: (key: ContactKey) => getValue({ group: 'Contact', key, defaultValue: getDefaultContactValue(key) }),
		getDocument: (key: DocumentKey) => getValue({ group: 'Document', key, defaultValue: getDefaultDocumentValue(key) }),
		getLabel: (key: LabelKey) => getValue({ group: 'Label', key, defaultValue: getDefaultLabelValue(key) }),
		getNews: (key: NewsKey) => getValue({ group: 'News', key, defaultValue: getDefaultNewsValue(key) }),
		getJob: (key: JobKey) => getValue({ group: 'Job', key, defaultValue: getDefaultJobValue(key) }),
		getPreview: (key: PreviewKey) => getValue({ group: 'Preview', key, defaultValue: getDefaultPreviewValue(key) }),
		getSearch: (key: SearchKey, replacements?: string[]) =>
			getValue({ group: 'Search', key, defaultValue: getDefaultSearchValue(key), replacements }),
		getExplainer: (key: ExplainerKey) => getValue({ group: 'Explainer', key, defaultValue: getDefaultExplainerValue(key) }),
		getLanguage: (key: LanguageKey) => getValue({ group: 'Language', key, defaultValue: getDefaultLanguageValue(key) }),
	};
};

export interface getDictionaryItemsProps {
	locale: string;
	preview?: boolean;
}
export const getDictionaryItems = async ({ locale, preview }: getDictionaryItemsProps): Promise<Dictionary.Items> => {
	return await getDictionaryFromCDA({ locale, preview });
};
